// B-60 — the pure half of "Procesar el día" (inside "Nuevo cierre"): a client's daily files, uploaded once for all its
// offices, and the day's progress across them.
//
// Nothing here names a client (the rule `cierreManual.js` states for this folder). Which files a
// day takes is the backend's answer: `getCierreDailyProgress` counts one entry per daily document
// key in `documents`, read from the client's declarations, even before anything was sent. A client
// with no daily documents gets an empty map, and "Nuevo cierre" keeps its office picker for it.
//
// Three things here break silently, and are why this is out of the screen:
//
//   1. **The first run needs every file; a correction needs one.** The backend accepts any
//      non-empty subset so a corrected report can be re-sent alone. A file is required only while
//      it has never been sent for that day — otherwise every office stays PARTIAL with that
//      document missing, and nothing on screen says why.
//   2. **An office with no rows is not an office that balanced.** `EMPTY` is what B-50 reports
//      when the shared file has no row for an office's code — a misregistered code looks exactly
//      like that. It is listed next to the failures, not folded into "leídas".
//   3. **The default date is yesterday in the client's own timezone**, not the browser's. A
//      business date is a calendar day where the offices are; at 8pm in Bogotá it is already
//      tomorrow in UTC.

import { correctionError } from "./cierreCorrections";

// Every status the backend may record for an attempt. `missing` is the bucket of "never tried".
const INTENTOS = ["OK", "EMPTY", "ERROR", "TIMEOUT"];
const FALLIDOS = ["ERROR", "TIMEOUT"];

// The day's document keys, in the order the backend lists them.
export const clavesDiarias = (progreso) => Object.keys(progreso?.documents || {});

// A date as YYYY-MM-DD in `timezone`. `en-CA` is the locale whose short date already has that
// shape; an unknown or missing zone falls back to the browser's rather than throwing.
const fechaEnZona = (fecha, timezone) => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(fecha);
  } catch {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(fecha);
  }
};

// The day before today, where the client is. The process runs on D+1, so this is what the screen
// opens on — a default the operator changes after a weekend or a holiday, never a rule.
export const fechaPorDefecto = (ahora = new Date(), timezone) => {
  const [y, m, d] = fechaEnZona(ahora, timezone).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
};

// B-101 — the daily documents no office reads (Banco Agrario's account 7395, read by the client's day
// reconciliation). The backend lists them as `clientOnly`; an older backend lists none.
export const clavesSoloCliente = (progreso) => progreso?.clientOnly || [];

// The keys that must come with the next send: those never tried for this day on any office.
// Before the first run every count is zero, so all of them are; after it, none is. A document no
// office reads is never tried on one, so it is never mandatory — it would be for ever otherwise.
export const clavesObligatorias = (progreso) =>
  clavesDiarias(progreso).filter((clave) => {
    if (clavesSoloCliente(progreso).includes(clave)) return false;
    const conteo = progreso.documents[clave] || {};
    return !INTENTOS.some((estado) => (conteo[estado] || 0) > 0);
  });

// B-101 — each daily document's declared format, as the backend says it (`formats`). Bancamía's are
// `.xls` and Banco Agrario's `.xlsx`; an older backend says none, which is Bancamía's `.xls`.
export const formatoDeClave = (progreso, clave) => progreso?.formats?.[clave] || "xls";
// Every format the day's slots take — what the "all the reports" zone accepts. Never empty: with no
// key known yet it is Bancamía's .xls, as before B-101, so the zone still filters a folder.
export const formatosDelDia = (progreso, claves = clavesDiarias(progreso)) => {
  const formatos = [...new Set(claves.map((clave) => formatoDeClave(progreso, clave)))];
  return formatos.length ? formatos : ["xls"];
};

// B-80 — the day was processed before: some daily document was tried on some office. Sending it
// again redoes it, which the screen confirms first.
export const diaProcesado = (progreso) =>
  clavesDiarias(progreso).some((clave) => INTENTOS.some((estado) => (progreso.documents[clave]?.[estado] || 0) > 0));

// `archivos` is `{ clave: File | null }`. At least one file, and every required one — except on a
// day processed before (B-80), which may be redone with no file: the stored reports are re-read.
export const puedeProcesar = (archivos, obligatorias, { procesado = false } = {}) => {
  const elegidas = Object.keys(archivos || {}).filter((clave) => archivos[clave]);
  if (elegidas.length === 0) return procesado;
  return (obligatorias || []).every((clave) => elegidas.includes(clave));
};

// B-80 — the progress to show the moment a run is sent: the day's documents (they decide the slots
// and that the client is a daily one) and nothing counted. The counters the day had before are the
// last run's, and drawing them made a redo read 100% from the first second.
export const avanceAlEnviar = (progreso) =>
  progreso
    ? {
        ...progreso,
        offices: { total: progreso.offices?.total || 0, read: 0, failed: 0, pending: progreso.offices?.total || 0 },
        closings: [],
      }
    : progreso;

// The progress bar and the list under it. `porcentaje` counts an office as done once it was read
// or failed: both are final for this run, and a bar that stops short of 100 on a failure reads as
// a run still going.
export const resumenDeAvance = (progreso) => {
  const oficinas = progreso?.offices || {};
  const total = oficinas.total || 0;
  const leidas = oficinas.read || 0;
  const fallidas = oficinas.failed || 0;
  const pendientes = oficinas.pending || 0;

  const conProblema = (progreso?.closings || [])
    .map((cierre) => {
      const documentos = Object.entries(cierre.documents || {});
      const fallaron = documentos.filter(([, estado]) => FALLIDOS.includes(estado)).map(([clave]) => clave);
      const vacios = documentos.filter(([, estado]) => estado === "EMPTY").map(([clave]) => clave);
      if (!fallaron.length && !vacios.length) return null;
      const claves = fallaron.length ? fallaron : vacios;
      return {
        closingId: cierre.closingId,
        officeCode: cierre.officeCode,
        tipo: fallaron.length ? "error" : "sinFilas",
        claves,
        // B-126 — why, said by the backend for the operator: which file, what happened, what to do.
        motivos: claves
          .map((clave) => (cierre.errors || {})[clave])
          .filter((motivo) => motivo && motivo.mensaje)
          .map(({ mensaje, accion }) => ({ mensaje, accion: accion || "" })),
      };
    })
    .filter(Boolean)
    // Failures first: they stop the office; an empty file is a warning to look at.
    .sort((a, b) => (a.tipo === b.tipo ? 0 : a.tipo === "error" ? -1 : 1));

  return {
    total,
    leidas,
    fallidas,
    pendientes,
    porcentaje: total ? Math.round(((leidas + fallidas) / total) * 100) : 0,
    conProblema,
  };
};

// A refusal of `runCierreDailyProcess`, in the operator's words. The backend's sentences are
// written for a developer reading a log ("Follow it with getCierreDailyProgress"), so the three an
// operator meets are said here; anything else is shown as the backend wrote it.
export const errorDeProceso = (response) => {
  // B-126 — a refused file said by the backend for the operator: which file, what, what to do.
  if (response?.mensaje) return `${response.mensaje} ${response.accion || ""}`.trim();
  switch (response?.errorCode) {
    case "CONFLICT":
      return "Ya hay un proceso corriendo para este cliente. Espera a que termine; el avance se actualiza solo.";
    case "DAY_ALREADY_PROCESSED":
      return "Este día ya se procesó: confirma para rehacerlo.";
    case "LIMIT_FILE_SIZE":
      return "Uno de los archivos supera el límite de 20 MB.";
    case "INVALID_ARGUMENT":
      // Sent with no file on a day never processed: nothing stored to read again.
      if (/^no files/i.test(response?.message || "")) {
        return "Este día todavía no se ha procesado: sube los reportes del cliente para procesarlo.";
      }
      return `Uno de los archivos no corresponde: revisa que cada reporte esté en su casilla y tenga el formato pedido. (${correctionError(response)})`;
    default:
      return correctionError(response);
  }
};
